// Auth API utilities
import { apiClient, ApiError } from '@/lib/api/client';

export interface RegisterRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export interface RegisterResponse {
  message: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  needs_onboarding: boolean;
  tokens: {
    access: string;
    refresh: string;
  };
}

export async function register(data: RegisterRequest): Promise<RegisterResponse> {
  // Use the dedicated public route handler at /api/register — the /api/proxy
  // route requires a valid JWT session and would 401 for unauthenticated users.
  // Plain fetch avoids apiClient's normalizeEndpoint stripping /api/ prefix.
  const response = await fetch('/api/register/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const d = json as Record<string, unknown>;
    // Surface DRF field-level validation errors as plain Error messages
    if (d.email) {
      const msg = d.email;
      throw new Error(Array.isArray(msg) ? (msg as string[])[0] : String(msg));
    }
    if (d.password) {
      const msg = d.password;
      throw new Error(Array.isArray(msg) ? (msg as string[])[0] : String(msg));
    }
    throw new ApiError(
      response.status,
      (d.detail as string) || (d.error as string) || (d.message as string) || 'Registration failed',
      d
    );
  }

  return json as RegisterResponse;
}

/**
 * Mint a signed cross-app token and get a redirect URL to the target peer app
 * (Valvomo or Silo). Returns 503 if that pair's secret or public URL is not
 * configured on the backend.
 *
 * The caller should `window.location.href = url` (full-page redirect, not window.open).
 */
export async function crossAppToken(target: 'valvomo' | 'silo' = 'valvomo'): Promise<{
  url: string;
}> {
  return apiClient.get<{ url: string }>(`/api/v1/auth/cross-app-token/?target=${target}`);
}

export interface CrossAppExchangeResponse {
  access: string;
  refresh: string;
  user: { id: string; email: string };
}

/**
 * Exchange a Valvomo-issued cross-app token for TimePoint JWT tokens.
 * The token is single-use and expires within 60 seconds.
 */
export async function crossAppExchange(token: string): Promise<CrossAppExchangeResponse> {
  return apiClient.post<CrossAppExchangeResponse>('/api/v1/auth/cross-app-exchange/', { token });
}
